/**
 * ReportDetailPage — viewer by default, form-builder editor on Edit.
 *
 * Loads the full report from /api/reports/:id (cached to sessionStorage
 * for sub-second second-visit load), and toggles between the read-only
 * ReportViewer and the draggable ReportEditor. Save PUTs the spec back.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Pencil, RefreshCw, Save, ArrowLeft } from 'lucide-react'

import { PageLayout } from '../components/PageLayout'
import { ReportViewer } from '../components/reports/ReportViewer'
import { ReportEditor } from '../components/reports/ReportEditor'
import type { ReportFull, ReportSpec } from '../components/reports/types'

const CACHE_TTL_MS = 2 * 60 * 1000
const reportCacheKey = (id: string | number) => `butler_report_meta_${id}`

function readCachedReport(id: string): ReportFull | null {
  try {
    const raw = window.sessionStorage?.getItem(reportCacheKey(id))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { at: number; report: ReportFull }
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null
    return parsed.report
  } catch {
    return null
  }
}

function writeCachedReport(id: string | number, report: ReportFull) {
  try {
    window.sessionStorage?.setItem(reportCacheKey(id), JSON.stringify({ at: Date.now(), report }))
  } catch {
    /* ignore */
  }
}

export function ReportDetailPage() {
  const { id: rawId } = useParams()
  const navigate = useNavigate()
  const id = rawId ?? ''
  const [report, setReport] = useState<ReportFull | null>(() => readCachedReport(id))
  const [loading, setLoading] = useState<boolean>(report === null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<boolean>(false)
  const [draftSpec, setDraftSpec] = useState<ReportSpec | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [refreshKey, setRefreshKey] = useState<number>(0)

  const loadReport = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await fetch(`/api/reports/${id}`, { credentials: 'same-origin' })
      if (!res.ok) {
        setError(res.status === 404 ? 'Report not found' : `Load failed (${res.status})`)
        setLoading(false)
        return
      }
      const body = (await res.json()) as { report: ReportFull }
      setReport(body.report)
      writeCachedReport(id, body.report)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    const cached = readCachedReport(id)
    if (cached) {
      setReport(cached)
      setLoading(false)
    } else {
      void loadReport()
    }
  }, [id, loadReport])

  function startEdit() {
    if (report) setDraftSpec(report.spec)
    setEditing(true)
  }

  async function save() {
    if (!report || !draftSpec) return
    setSaving(true)
    try {
      const res = await fetch(`/api/reports/${report.id}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spec: draftSpec, name: draftSpec.name }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError(body?.message || `save failed (${res.status})`)
        setSaving(false)
        return
      }
      const body = (await res.json()) as { report: ReportFull }
      setReport(body.report)
      writeCachedReport(report.id, body.report)
      setEditing(false)
      setDraftSpec(null)
      setRefreshKey((k) => k + 1)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageLayout title="Report" subtitle="Loading…">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
          Loading report…
        </div>
      </PageLayout>
    )
  }

  if (error || !report) {
    return (
      <PageLayout title="Report" subtitle={error ?? 'Not found'}>
        <button
          type="button"
          onClick={() => navigate('/reports')}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to reports
        </button>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={report.name}
      subtitle={report.description ?? 'Custom report'}
      actions={
        <div className="flex gap-2">
          {!editing && (
            <>
              <button
                type="button"
                onClick={() => setRefreshKey((k) => k + 1)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                data-testid="report-refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Refresh
              </button>
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700"
                data-testid="report-edit"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Edit
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditing(false)
                  setDraftSpec(null)
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                Discard
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={save}
                className="inline-flex items-center gap-1.5 rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700 disabled:opacity-50"
                data-testid="report-save"
              >
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      }
    >
      {editing && draftSpec ? (
        <ReportEditor value={draftSpec} onChange={setDraftSpec} />
      ) : (
        <ReportViewer report={report} refreshKey={refreshKey} />
      )}
    </PageLayout>
  )
}
